/**
 * Uploading images straight from the browser to S3, through a short-lived upload link
 * from the API, with progress.
 */

import { api } from './api'

export const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
export const MAX_BYTES = 10 * 1024 * 1024
export const MAX_IMAGES = 10
export const LIMITS_HINT = 'JPG, PNG, GIF or WebP. Up to 10 MB each, up to 10 images.'

/** Why a file can't be added, or null if it's fine */
export function rejectReason(file: File): string | null {
  if (!IMAGE_TYPES.includes(file.type)) return 'Only JPG, PNG, GIF and WebP images can be uploaded.'
  if (file.size > MAX_BYTES) return 'Images can be up to 10 MB each.'
  return null
}

export function formatSize(bytes: number): string {
  return bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`
}

/** Upload one file; resolves with its stored key */
export async function uploadImage(file: File, onProgress: (percent: number) => void, signal: AbortSignal): Promise<string> {
  const { key, uploadUrl } = await api.post<{ key: string; uploadUrl: string }>('/api/admin/uploads', {
    fileName: file.name,
    contentType: file.type,
    fileSize: file.size,
  })
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    const abort = () => xhr.abort()
    signal.addEventListener('abort', abort)
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(Math.round((e.loaded / e.total) * 100))
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error('The upload was refused.')))
    xhr.onerror = () => reject(new Error('The connection was lost.'))
    xhr.onabort = () => reject(new DOMException('Upload cancelled', 'AbortError'))
    xhr.onloadend = () => signal.removeEventListener('abort', abort)
    xhr.open('PUT', uploadUrl)
    xhr.setRequestHeader('Content-Type', file.type)
    xhr.send(file)
  })
  return key
}
